/* useReportingPeriod.ts */
import { useEffect, useState } from 'react'
import moment from 'moment'
import useStore from '../store/useStore'

const DEFAULT_DAY_OF_MONTH_LIMIT = 15

interface ReportingPeriodResult {
  dayOfMonthLimit: number
  pastDeadline: boolean
  currentMonth: boolean
  monthMoment: moment.Moment
  month: string
  changeToMoment: moment.Moment
  isReady: boolean
}

/**
 * A hook that calculates the reporting period based on the dayOfMonthLimit fetched from the server
 * @returns Object containing reporting period information and a flag indicating if data is ready
 */
const useReportingPeriod = (): ReportingPeriodResult => {
  const [state, setState] = useState<ReportingPeriodResult>({
    dayOfMonthLimit: DEFAULT_DAY_OF_MONTH_LIMIT,
    pastDeadline: false,
    currentMonth: false,
    monthMoment: moment(),
    month: '',
    changeToMoment: moment(),
    isReady: false,
  })

  // Get the meta data from the store, which will be loaded from Firestore
  const meta = useStore((s) => s.meta)

  useEffect(() => {
    // Only update the state when meta data is available
    if (meta !== undefined) {
      const m = moment()

      // Use dayOfMonthLimit from meta or default to 15
      const dayOfMonthLimit =
        meta?.dayOfMonthLimit || DEFAULT_DAY_OF_MONTH_LIMIT

      // Validate that dayOfMonthLimit is a valid number
      const validDayOfMonthLimit =
        typeof dayOfMonthLimit === 'number' &&
        !isNaN(dayOfMonthLimit) &&
        dayOfMonthLimit > 0 &&
        dayOfMonthLimit <= 31
          ? dayOfMonthLimit
          : DEFAULT_DAY_OF_MONTH_LIMIT

      // Calculate if the current date is past the deadline
      const pastDeadline = m.date() > validDayOfMonthLimit

      // Determine whether to show current month or previous month
      const currentMonth = pastDeadline

      // Create the month moment object
      const monthMoment = m.clone().add(currentMonth ? 0 : -1, 'months')

      // Format the month for API calls
      const month = m
        .clone()
        .add(currentMonth ? 0 : -1, 'months')
        .format('YYYY-MM')

      // Calculate the alternate month to switch to
      const changeToMoment = m.clone().add(currentMonth ? -1 : 0, 'month')

      // Update the state with all the calculated values
      setState({
        dayOfMonthLimit: validDayOfMonthLimit,
        pastDeadline,
        currentMonth,
        monthMoment,
        month,
        changeToMoment,
        isReady: true,
      })
    }
  }, [meta])

  return state
}

export default useReportingPeriod
